import { Component, Element, Host, Prop, State, Watch, h, getAssetPath, JSX } from '@stencil/core';
import { Store } from '../../models/stores/Store';
import { GoogleMapsLoader } from '../../utils/services/googleMapsLoader';
import { storeService } from '../../utils/services/storeService';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { brandService } from '../../utils/services/brandService';
import { z } from 'zod';
import { ASL_StoreInfoCardProps } from '../aico-store-locator-store-info-card/aico-store-locator-store-info-card';
import { ASL_HoursDisplayProps } from '../aico-store-locator-hours-display/aico-store-locator-hours-display';
import { ASL_ImageCarouselProps } from '../aico-store-locator-image-carousel/aico-store-locator-image-carousel';
import { translationService, TranslationsProps } from '../../utils/services/translationService';

export const Direction = z.enum(['left', 'right', 'top', 'bottom']);
export type Direction = z.infer<typeof Direction>;
export const ActionButton = z.enum(['share', 'email', 'phone', 'location']);
export type ActionButton = z.infer<typeof ActionButton>;
export const ActionButtonsStyle = z.enum(['default', 'labeled']);
export type ActionButtonsStyle = z.infer<typeof ActionButtonsStyle>;
export const ActionButtonsPosition = z.enum(['default', 'kybun']);
export type ActionButtonsPosition = z.infer<typeof ActionButtonsPosition>;
export const FooterStoreInfoCardBehavior = z.enum(['default', 'permanent', 'hidden']);
export type FooterStoreInfoCardBehavior = z.infer<typeof FooterStoreInfoCardBehavior>;
export const FooterStoreInfoCardAnimation = z.enum(['default', 'slight', 'none']);
export type FooterStoreInfoCardAnimation = z.infer<typeof FooterStoreInfoCardAnimation>;

export const ASL_DetailedViewProps = z.object({
  openFrom: Direction.optional(),
  actionButtonsPosition: ActionButtonsPosition.optional(),
  actionButtonsStyle: ActionButtonsStyle.optional(),
  excludedActionButtons: z.array(ActionButton).optional(),
  componentColors: ComponentColors.optional(),
  translationsProps: TranslationsProps.optional(),

  ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
  ASL_HoursDisplayProps: ASL_HoursDisplayProps.optional(),
  ASL_ImageCarouselProps: ASL_ImageCarouselProps.optional(),
  Mobile: z.object({
    openFrom: Direction.optional(),
    actionButtonsPosition: ActionButtonsPosition.optional(),
    actionButtonsStyle: ActionButtonsStyle.optional(),
    footerStoreInfoCardBehavior: FooterStoreInfoCardBehavior.optional(),
    footerStoreInfoCardAnimation: FooterStoreInfoCardAnimation.optional(),
    ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
    ASL_HeaderStoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
    ASL_HoursDisplayProps: ASL_HoursDisplayProps.optional(),
    ASL_ImageCarouselProps: ASL_ImageCarouselProps.optional(),
    ASL_FooterStoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
  }).optional(),
  Desktop: z.object({
    openFrom: Direction.optional(),
    ASL_StoreInfoCardProps: ASL_StoreInfoCardProps.optional(),
    ASL_HoursDisplayProps: ASL_HoursDisplayProps.optional(),
    ASL_ImageCarouselProps: ASL_ImageCarouselProps.optional(),
  }).optional(),
});
export type ASL_DetailedViewProps = z.infer<typeof ASL_DetailedViewProps>;

@Component({
  tag: 'aico-store-locator-detailed-view',
  styleUrl: 'aico-store-locator-detailed-view.css',
  shadow: true,
  assetsDirs: ['../assets']
})
export class AicoStoreLocatorDetailedView {
  private mapElement?: HTMLElement;
  private map?: google.maps.Map;
  private marker?: google.maps.marker.AdvancedMarkerElement;
  @Prop() mapId!: string;

  /** Controls whether the popup is open */
  @Prop() open: boolean = false;

  /** Direction from which the popup appears. Default: 'left' */
  @Prop() openFrom: Direction = 'left';

  @Prop({ mutable: true }) store?: Store;
  private storeLogos: string[] = [];

  @Prop() actionButtonsPosition: ActionButtonsPosition = 'default';
  @Prop() actionButtonsOrder?: Partial<Record<ActionButton, number>>;
  private BASE_ORDER: Record<ActionButton, number> = {
    location: 1,
    phone: 2,
    email: 3,
    share: 4,
  };
  private shrinkMap: Record<ActionButton, number> = {
    location: 0,
    phone: 1,
    email: 2,
    share: 3,
  };
  private getOrderedButtons(): ActionButton[] {
    const order = { ...this.BASE_ORDER, ...this.actionButtonsOrder };
    return (Object.keys(order) as ActionButton[])
      .sort((a, b) => order[a] - order[b]);
  }
  @Prop() actionButtonsStyle: ActionButtonsStyle = 'default';
  @Prop() mobileActionButtonsPosition: ActionButtonsPosition = 'default';
  @Prop() excludeActionButtons: ActionButton[] = [];
  @Prop({ mutable: true }) footerStoreInfoCardBehavior: FooterStoreInfoCardBehavior = 'default';
  @Prop() footerStoreInfoCardAnimation: FooterStoreInfoCardAnimation = 'default';

  @Prop() componentColors?: ComponentColors;

  @Prop() props?: ASL_DetailedViewProps;

  @State() mapLoaded: boolean = false;
  @State() isMobile: boolean = false;
  @State() shareCopied: boolean = false;
  @State() phoneCopied: boolean = false;
  @State() isContactFormOpen: boolean = false;

  @State() headerVisible: boolean = true;
  private headerRef?: HTMLElement;
  private headerObserver?: IntersectionObserver;
  @State() footerContentHeight: number = 0;
  private infoCardEl?: HTMLElement;

  private mediaQuery!: MediaQueryList;
  @Prop() languageKey?: string;
  @Prop() language?: string;
  private unsubscribeController!: AbortController;

  private addStoreIdToUrl(storeId: string | number) {
    const url = new URL(window.location.href);
    url.searchParams.set('asl_s_id', storeId.toString());
    window.history.pushState({ storeOpen: true, storeId: storeId }, '', url);
    window.addEventListener('popstate', this.onPopState, { signal: this.unsubscribeController.signal });
  }

  private removeStoreIdFromUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('asl_s_id');
    window.history.replaceState({}, '', url);
    if (history.state && history.state.storeOpen) {
      history.back();
    }
  }

  private onPopState = () => {
    if (this.open) {
      this.closePopup();
    }
  };

  @Watch('open')
  openChanged(newVal: boolean) {
    if (newVal && this.store) {
      this.addStoreIdToUrl(this.store.id);
    } else {
      this.removeStoreIdFromUrl();
    }
  }

  @Element() el!: HTMLElement;

  async componentWillLoad() {
    if (this.props) {
      this.props = ASL_DetailedViewProps.parse(this.props);
      this.openFrom = this.props.openFrom ?? this.openFrom;
      this.actionButtonsStyle = this.props.actionButtonsStyle ?? this.actionButtonsStyle;
      this.actionButtonsPosition = this.props.actionButtonsPosition ?? this.actionButtonsPosition;
      this.excludeActionButtons = this.props.excludedActionButtons ?? this.excludeActionButtons;
      this.footerStoreInfoCardBehavior = this.props?.Mobile?.footerStoreInfoCardBehavior ?? this.footerStoreInfoCardBehavior;
      this.footerStoreInfoCardAnimation = this.props?.Mobile?.footerStoreInfoCardAnimation ?? this.footerStoreInfoCardAnimation;
      this.mobileActionButtonsPosition = this.props?.Mobile?.actionButtonsPosition ?? this.mobileActionButtonsPosition;
      this.componentColors = this.props.componentColors ?? this.componentColors;
    }

    await translationService.initComponentLoad('aico-store-locator-detailed-view');

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }
    this.unsubscribeController?.abort();
    this.unsubscribeController = new AbortController();

    storeService.subscribeToSelectedStore(
      (store: Store | undefined) => {
        if (store) {
          this.storeLogos = brandService.getLogosOrderedByPriority(store.crmAddress?.storeLocatorBrandIds);
          this.openStoreDetails(store);
        }
      },
      false,
      { signal: this.unsubscribeController.signal }
    )

    this.mediaQuery = window.matchMedia('(max-width: 768px)');
    this.isMobile = this.mediaQuery.matches;
    this.updateDynamicOpenFrom();
    this.mediaQuery.addEventListener('change', (e: MediaQueryListEvent) => {
      const old = this.isMobile;
      this.isMobile = e.matches;
      if (this.isMobile !== old) {
        setTimeout(() => {
          this.updateDynamicOpenFrom();
          this.initializeMap()
        }, 0);
      }
    }, { signal: this.unsubscribeController.signal });
  }

  private updateDynamicOpenFrom() {
    if (this.isMobile) {
      this.openFrom = this.props?.Mobile?.openFrom || this.props?.openFrom || 'left';
    }
    else {
      this.openFrom = this.props?.Desktop?.openFrom || this.props?.openFrom || 'left';
    }
  }

  private t(key:string, locale?:string): string {
    return translationService.getTranslation('aico-store-locator-detailed-view', key, locale);
  }

  componentDidLoad() {
    GoogleMapsLoader.getInstance()
      .load()
      .then(() => {
        this.mapLoaded = true;
        if (this.store) {
          this.initializeMap();
        }
      })
      .catch(err => {
        console.error(err);
      });
  }

  componentWillRender() {
    if (this.isMobile && this.headerRef) {
      this.headerObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.headerVisible = entry.isIntersecting;
        });
      }, { threshold: 0.1 });
      this.headerObserver.observe(this.headerRef);
    }
    else {
      this.headerObserver?.disconnect();
    }
  }

  componentDidRender() {
    if (this.infoCardEl) {
      const measuredHeight = this.infoCardEl.scrollHeight;
      if (measuredHeight !== this.footerContentHeight) {
        this.footerContentHeight = measuredHeight;
      }
    }
  }

  disconnectedCallback() {
    this.unsubscribeController?.abort();
    this.headerObserver?.disconnect();
  }

  private openStoreDetails(store: Store) {
    this.store = store;
    this.open = true;

    this.mapElement = undefined;

    this.initializeMap();
  }

  private async initializeMap() {
    if (!this.mapElement) {
      const fallback = document.querySelector('.persistent-map');
      if (fallback) {
        this.mapElement = fallback as HTMLElement;
      }
    }

    if (!this.mapElement || !this.store) return;

    const storeLocation = {
      lat: this.store.latitude,
      lng: this.store.longitude
    };

    const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;

    const mapOptions: google.maps.MapOptions = {
      mapId: this.mapId,
      center: storeLocation,
      zoom: 15,

      mapTypeId: google.maps.MapTypeId.ROADMAP,
      disableDefaultUI: this.isMobile,
      zoomControl: !this.isMobile,
      streetViewControl: !this.isMobile,
      clickableIcons: false,
      draggable: false,
    };

    this.map = new Map(this.mapElement, mapOptions);
    this.updateMarker();
  }

  private openGoogleMapsDirections() {
    if (!this.store) return;

    // Construct Google Maps directions URL
    const directionsUrl = new URL('https://www.google.com/maps/dir/');
    directionsUrl.searchParams.append('api', '1');
    directionsUrl.searchParams.append('destination', `${this.store.latitude},${this.store.longitude}`);

    // Open Google Maps in a new tab
    window.open(directionsUrl.toString(), '_blank');
  }

  private updateMarker() {
    if (!this.map || !this.store) return;

    if (this.marker) {
      this.marker.map = null;
    }

    const storeLocation = {
      lat: this.store.latitude,
      lng: this.store.longitude
    };

    // Create marker content
    const markerContent = document.createElement('div');
    const img = document.createElement('img');
    img.src = getAssetPath(brandService.getHighestPriorityBrand(this.store?.crmAddress?.storeLocatorBrandIds)?.pinUrl || '');
    img.alt = "Store Location";
    img.className = 'w-6 h-8 text-[16px]';
    markerContent.appendChild(img);

    // Create new marker with click functionality
    this.marker = new google.maps.marker.AdvancedMarkerElement({
      position: storeLocation,
      map: this.map,
      title: this.store.organisationName,
      content: markerContent,
    });

    // Add click event listener for directions
    google.maps.event.addListener(this.marker, 'click', () => {
      if (!this.store) return;
      this.openGoogleMapsDirections();
    });
  }

  private closePopup = () => {
    this.open = false;
    setTimeout(() => {
      this.store = undefined;
    });
  }

  private getTransformClasses() {
    const transforms = {
      left: { closed: '-translate-x-full', open: 'translate-x-0' },
      right: { closed: 'translate-x-full', open: 'translate-x-0' },
      top: { closed: '-translate-y-full', open: 'translate-y-0' },
      bottom: { closed: 'translate-y-full', open: 'translate-y-0' },
    };
    return this.open ? transforms[this.openFrom].open : transforms[this.openFrom].closed;
  }

  private getPositionClasses() {
    switch (this.openFrom) {
      case 'right':
        return 'right-0 top-0';
      case 'top':
        return 'top-0 left-0';
      case 'bottom':
        return 'bottom-0 left-0';
      case 'left':
      default:
        return 'left-0 top-0';
    }
  }

  private getDimensionClasses() {
    if (this.openFrom === 'left' || this.openFrom === 'right') {
      return 'w-full lg:w-3/4 lg:max-w-[1200px] h-full';
    } else {
      return 'w-full h-full lg:h-4/5';
    }
  }

  private canRenderActionButton(actionButton: ActionButton) {
    return !this.excludeActionButtons.includes(actionButton);
  }

  private buildActionBtn(kind: ActionButton,
    onClick: () => void,
    icon: JSX.Element,
    label: string) {

    const labeledBase =
      'flex items-center justify-center gap-1 min-w-0 ' +
      'flex-1 basis-0 max-w-1/3 shrink ' +
      'px-3 py-2 rounded-full ' +
      'bg-[--primary-button-background-color] ' +
      'text-[--primary-button-text-color] ' +
      'container-type-inline-size' +
      '@container';

    const cls = this.actionButtonsStyle === 'labeled'
      ? `${labeledBase}`
      : 'flex items-center justify-center ' +
      'w-10 h-10 rounded-full ' +
      'bg-[--secondary-button-background-color] ' +
      'hover:bg-[--secondary-button-hover-color] ' +
      'text-[--secondary-button-text-color]' +
      '@container';

    return (
      <button
        class={cls}
        style={{ flexShrink: this.shrinkMap[kind].toString() }}
        onClick={onClick}
      >
        {icon}
        {this.actionButtonsStyle === 'labeled' && (
          <span class="@[4rem]:hidden whitespace-nowrap">
            {label}
          </span>
        )}
      </button>
    );
  }



  private async sharePage() {
    try {
      await navigator.share({ url: window.location.href });
    } catch {
      try {
        await navigator.clipboard.writeText(window.location.href);
      } catch (error) {
        console.error("Failed to copy URL to clipboard:", error);
        return;
      }
    }

    this.shareCopied = true;
    setTimeout(() => (this.shareCopied = false), 2000);
  }

  private renderShareButton() {
    return (
      <button
        onClick={() => this.sharePage()}
        class="flex justify-center items-center bg-[--secondary-button-background-color] hover:bg-[--secondary-button-hover-color] rounded-full w-10 h-10 text-[--secondary-button-text-color] transition-colors"
      >
        {this.shareCopied ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5 animate-copied"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <circle cx="18" cy="5" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <circle cx="6" cy="12" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <circle cx="18" cy="19" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        )}
      </button>
    )

  }

  private openContactForm() {
    this.isContactFormOpen = true;
  }

  private handleContactFormClosed = () => {
    this.isContactFormOpen = false;
  }

  private renderEmailButton() {
    return (
      <button
        onClick={() => this.openContactForm()}
        class="flex justify-center items-center bg-[--secondary-button-background-color] hover:bg-[--secondary-button-hover-color] rounded-full w-10 h-10 text-[--secondary-button-text-color] transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </button>
    )
  }

  private async sharePhone() {
    if (!this.store?.phone) return;
    try {
      window.open(`tel:${this.store.phone}`, 'noopener,noreferrer');
    } catch {
      try {
        await navigator.clipboard.writeText(this.store.phone);
      } catch (error) {
        console.error("Failed to copy phone number:", error);
        return;
      }
    }

    this.phoneCopied = true;
    setTimeout(() => (this.phoneCopied = false), 2000);
  }

  private renderPhoneButton() {
    return (
      <button
        onClick={() => { this.sharePhone() }}
        class="flex justify-center items-center bg-[--secondary-button-background-color] hover:bg-[--secondary-button-hover-color] rounded-full w-10 h-10 text-[--secondary-button-text-color] transition-colors"
      >
        {this.phoneCopied ? (
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 animate-copied" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        )}
      </button>
    );
  }

  private renderLocationButton() {
    return (
      <button
        onClick={() => this.openGoogleMapsDirections()}
        class="flex justify-center items-center bg-[--secondary-button-background-color] hover:bg-[--secondary-button-hover-color] rounded-full w-10 h-10 text-[--secondary-button-text-color] transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
    )
  }

  private renderActionButtons() {
    return (
      <div class="flex flex-col items-center gap-4 p-2">
        {/* Close button */}
        <button
          onClick={this.closePopup}
          class="flex justify-center items-center bg-[--secondary-button-background-color] hover:bg-[--secondary-button-hover-color] p-2 rounded-full w-10 h-10 text-[--secondary-button-text-color] transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Action buttons pill */}
        {this.actionButtonsPosition !== 'kybun' && (this.canRenderActionButton('share') ||
          (this.canRenderActionButton('email') && this.store?.email) ||
          (this.canRenderActionButton('phone') && this.store?.phone)) &&
          (<div class="flex flex-col gap-2 bg-[--secondary-button-background-color] p-2 rounded-full">
            {this.canRenderActionButton('share') && this.renderShareButton()}
            {this.canRenderActionButton('email') && this.store?.email && (this.renderEmailButton())}
            {this.canRenderActionButton('phone') && this.store?.phone && (this.renderPhoneButton())}
            {this.canRenderActionButton('location') && this.store && (this.renderLocationButton())}
          </div>)}
      </div>
    );
  }

  private renderMap() {
    return (
      <div
        ref={el => {
          if (el) {
            if (this.mapElement !== el) {
              this.mapElement = el;
              if (this.mapLoaded && this.store) {
                this.initializeMap();
              }
            }
          }
        }}
        class="rounded-lg w-full h-full overflow-hidden persistent-map"
      />
    );
  }

  private hasImages(): boolean {
    return !!this.store?.crmAddress?.b2bCalendar?.b2bCalendarImages?.length;
  }

  render() {
    if (this.isMobile) {
      return (
        <Host>
          <div
            class={`fixed inset-0 z-[150] overflow-hidden transition-opacity duration-500 ${this.open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          >
            <div
              class={[
                'absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300',
                this.open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              ].join(' ')}
              onClick={this.closePopup}
            ></div>
            <div
              class={[
                'absolute shadow-xl transition-transform duration-500 ease-in-out',
                'bg-white',
                'w-full',
                'h-full',
                'flex',
                'flex-col',
                'justify-between',
                this.getTransformClasses(),
                this.getPositionClasses()
              ].join(' ')}
            >
              {/* Header */}
              <header class="top-0 z-10 sticky flex-none bg-white p-2 border-b" ref={(el) => this.headerRef = el as HTMLElement}>
                <button class="flex items-center gap-2 p-2 text-[--secondary-button-text-color]" onClick={this.closePopup}>
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  <span class="font-medium text-sm">{this.t('Back')}</span>
                </button>
              </header>
              <main class="flex-1 overflow-y-auto no-scrollbar">
                {/* Store info */}
                <div ref={(el) => this.headerRef = el as HTMLElement} class="p-1 w-full">
                  <aico-store-locator-store-info-card
                    refKey={this.store?.id}
                    organisationName={this.store?.organisationName || ''}
                    addressLine={this.store?.addressLine1 || ''}
                    zipCode={this.store?.zipCode || ''}
                    city={this.store?.city || ''}
                    distance={this.store?.distance}
                    brandsLogos={this.storeLogos}
                    isMobile={true}
                    class="p-0"
                    props={this.props?.Mobile?.ASL_HeaderStoreInfoCardProps ||
                      this.props?.Mobile?.ASL_StoreInfoCardProps ||
                      this.props?.ASL_StoreInfoCardProps
                    }
                  />
                </div>

                {/* Map */}
                <div class="w-full h-64">
                  {this.renderMap()}
                </div>

                {/* Timetable */}
                {this.store?.crmAddress.b2bCalendar?.b2bCalendarDays?.length != 0 && (
                  <div class="p-4 w-full">
                    <aico-store-locator-hours-display
                      calendar={this.store?.crmAddress.b2bCalendar}
                      isExpandable={true}
                      isExpanded={false}
                      props={this.props?.Mobile?.ASL_HoursDisplayProps ||
                        this.props?.ASL_HoursDisplayProps}
                    />
                  </div>
                )}

                {/* Notice */}
                {this.store?.crmAddress?.b2bCalendar?.notice && (
                  <div class="mt-2 test-[--secondary-text-color] text-base">
                    <span>
                      {this.store?.crmAddress?.b2bCalendar?.notice}
                    </span>
                  </div>
                )}


                {/* Image carousel if available */}
                {this.hasImages() && (
                  <div class="py-4 w-full h-96">
                    <aico-store-locator-image-carousel images={this.store?.crmAddress?.b2bCalendar?.b2bCalendarImages || []}
                      props={this.props?.Mobile?.ASL_ImageCarouselProps ||
                        this.props?.ASL_ImageCarouselProps
                      } />
                  </div>
                )}

                {/* Events section */}
                {this.store?.additionalUrl && (
                  <div class="">
                    <iframe id="CS_portal_5d243cf488c930718581f585" src={this.store.additionalUrl}
                      width="100%" height="600px" frameborder="0" scrolling="yes" class="p-2 overflow-hidden">
                    </iframe>
                  </div>
                )}
              </main>

              {/* Footer in mobile view */}
              <footer class={`${(this.footerStoreInfoCardBehavior === 'permanent' ||
                (this.footerStoreInfoCardBehavior === 'default' && !this.headerVisible) ||
                this.mobileActionButtonsPosition === 'default') ? 'flex-none bg-white border-t p-2' : ''}`}>
                <div
                  class={`overflow-hidden
                    ${this.footerStoreInfoCardAnimation === 'none'
                      ? ''
                      : 'transition-all duration-300 ease-in-out'}
                      `}
                  style={{
                    height:
                      (this.footerStoreInfoCardBehavior === 'permanent' ||
                        (this.footerStoreInfoCardBehavior === 'default' && !this.headerVisible))
                        ? `${this.footerContentHeight}px`
                        : '0px'
                  }}
                >
                  <div
                    ref={(el) => (this.infoCardEl = el as HTMLElement)}
                    class={`
                      ${this.footerStoreInfoCardAnimation === 'none'
                        ? ''
                        : 'transition-all duration-300 ease-in-out transform origin-bottom'}
                      ${this.footerStoreInfoCardBehavior === 'permanent' ||
                        (this.footerStoreInfoCardBehavior === 'default' && !this.headerVisible)
                        ? 'translate-y-0 opacity-100 scale-y-100'
                        : this.footerStoreInfoCardAnimation === 'slight'
                          ? 'translate-y-full opacity-100 scale-y-100'
                          : 'translate-y-full opacity-0 scale-y-0'
                      }`}
                  >
                    <aico-store-locator-store-info-card
                      refKey={this.store?.id}
                      organisationName={this.store?.organisationName || ''}
                      brandsLogos={this.storeLogos}
                      isMobile={true}
                      class="p-0"
                      props={
                        this.props?.Mobile?.ASL_FooterStoreInfoCardProps ||
                        this.props?.Mobile?.ASL_StoreInfoCardProps ||
                        this.props?.ASL_StoreInfoCardProps
                      }
                    />
                  </div>
                </div>

                {/* Footer action buttons – always visible */}
                <div class="flex flex-nowrap justify-between items-center gap-2">
                  {this.getOrderedButtons().map(kind => {
                    if (!this.canRenderActionButton(kind)) return null;

                    switch (kind) {
                      case 'share':
                        return this.buildActionBtn('share',
                          () => this.sharePage(),
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <circle cx="18" cy="5" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <circle cx="6" cy="12" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <circle cx="18" cy="19" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>, this.t('Share'));
                      case 'email':
                        return this.buildActionBtn('email',
                          () => this.openContactForm(),
                          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>, this.t('E-Mail'));
                      case 'phone':
                        return this.buildActionBtn('phone',
                          () => this.sharePhone(),
                          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>, this.t('Phone'));
                      case 'location':
                      default:
                        return this.buildActionBtn('location',
                          () => this.openGoogleMapsDirections(),
                          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>, this.t('Directions'));
                    }
                  })}
                </div>
              </footer>
            </div>
          </div>
          <aico-store-locator-contact-form
            open={this.isContactFormOpen}
            store={this.store!}
            componentColors={this.componentColors}
            onFormClosed={this.handleContactFormClosed}
          />
        </Host>
      );
    }

    const verticalLayout = (
      <div class="[&::-webkit-scrollbar-thumb]:bg-[--scrollbar-thumb-color] [&::-webkit-scrollbar-track]:bg-[--scrollbar-track-color] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar]:w-2 h-full overflow-auto">
        <div class="flex flex-col px-4 min-h-[600px]">
          <div class="flex gap-4 mt-4 min-h-[400px]">
            <div class="relative marker:flex flex-col w-2/4 min-w-80">
              <aico-store-locator-store-info-card
                refKey={this.store?.id}
                organisationName={this.store?.organisationName || ''}
                addressLine={this.store?.addressLine1 || ''}
                zipCode={this.store?.zipCode || ''}
                city={this.store?.city || ''}
                distance={this.store?.distance}
                brandsLogos={this.storeLogos}
                props={this.props?.Desktop?.ASL_StoreInfoCardProps ||
                  this.props?.ASL_StoreInfoCardProps
                }
              />
              <div class="mt-2 max-w-96">
                <aico-store-locator-hours-display
                  calendar={this.store?.crmAddress.b2bCalendar}
                  props={this.props?.Desktop?.ASL_HoursDisplayProps ||
                    this.props?.ASL_HoursDisplayProps}
                ></aico-store-locator-hours-display>
              </div>
              {this.actionButtonsPosition == "kybun" && (<div class="flex flex-nowrap gap-2 w-full max-w-96 overflow-hidden">
                {this.getOrderedButtons().map(kind => {
                  if (!this.canRenderActionButton(kind)) return null;

                  switch (kind) {
                    case 'share':
                      return this.buildActionBtn('share',
                        () => this.sharePage(),
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <circle cx="18" cy="5" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          <circle cx="6" cy="12" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          <circle cx="18" cy="19" r="3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>, this.t('Share'));
                    case 'email':
                      return this.buildActionBtn('email',
                        () => this.openContactForm(),
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>, this.t('E-Mail'));
                    case 'phone':
                      return this.buildActionBtn('phone',
                        () => this.sharePhone(),
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>, this.t('Phone'));
                    case 'location':
                    default:
                      return this.buildActionBtn('location',
                        () => this.openGoogleMapsDirections(),
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>, this.t('Directions'));
                  }
                })}
              </div>)}
              {this.store?.crmAddress?.b2bCalendar?.notice && (
                <div class="mt-2 test-[--secondary-text-color] text-base">
                  <span>
                    {this.store?.crmAddress?.b2bCalendar?.notice}
                  </span>
                </div>
              )}
            </div>

            {this.hasImages() ? (
              <div class="flex justify-center items-center w-2/4 h-auto max-h-[500px]">
                <aico-store-locator-image-carousel
                  images={
                    this.store?.crmAddress?.b2bCalendar?.b2bCalendarImages || []
                  }
                  props={this.props?.Desktop?.ASL_ImageCarouselProps ||
                    this.props?.ASL_ImageCarouselProps
                  }
                />
              </div>
            ) : (
              <div class="w-2/4 h-auto max-h-[500px]">
                {this.renderMap()}
              </div>
            )}
          </div>

          {this.hasImages() && (
            <div class="mt-4 h-[300px]">
              {this.renderMap()}
            </div>
          )}

          {/* Events section */}
          {this.store?.additionalUrl && (
            <iframe id="CS_portal_5d243cf488c930718581f585" src={this.store.additionalUrl}
              width="100%" height="600px" frameborder="0" scrolling="yes" class="my-4 border-0 overflow-hidden">
            </iframe>
          )}
        </div>
      </div>
    );

    return (
      <Host>
        <div class={
          `fixed inset-0 z-[150] overflow-hidden transition-opacity duration-500
          ${this.open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`
        }>
          {/* Overlay */}
          <div
            class={[
              'absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300',
              this.open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            ].join(' ')}
            onClick={this.closePopup}
          ></div>

          {/* Popup container */}
          <div
            class={[
              'absolute bg-white shadow-xl',
              'transition-transform duration-500 ease-in-out',
              'scroll-smooth',
              this.getTransformClasses(),
              this.getTransformClasses(),
              this.getPositionClasses(),
              this.getDimensionClasses()
            ].join(' ')}
          >
            {/* Main content */}
            <div class="h-full">
              <div class="flex h-full">
                {this.openFrom === 'right' && <div class="flex-none">{this.renderActionButtons()}</div>}
                <div class="flex-1">
                  {verticalLayout}
                </div>
                {this.openFrom !== 'right' && <div class="flex-none">{this.renderActionButtons()}</div>}
              </div>
            </div>
          </div>
        </div>
        <aico-store-locator-contact-form
          open={this.isContactFormOpen}
          store={this.store}
          componentColors={this.componentColors}
          onFormClosed={this.handleContactFormClosed}
        />
      </Host>
    );
  }
}
